function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import "core-js/modules/web.dom-collections.iterator.js";
import React, { useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import { unstable_useId as useId } from '@mui/utils';
import TextField, { InputBase, PrefixContainer } from '../TextField';
import Box from '../Box';
import RangeBetweenIcon from '@pixel/assets/icons/RangeBetween';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '../IconButton';
import ClearInputIcon from '@pixel/assets/icons/ClearInput';
import { styled } from '../styles';
const RangeBetweenBox = styled(Box)(_ref => {
  let {
    theme
  } = _ref;
  return {
    width: theme.spacing(4),
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  };
});
const MAX_NUMBER = 9999999999; //limited because trying parseInt(9999999999999999) returns 10000000000000000 and cant handle a number larger. We dont need to support this in the IxD
const MIN_NUMBER = -MAX_NUMBER;
const NumberRangeField = props => {
  const {
    id: idOverride,
    min = null,
    //infinite limits to the MIN_NUMBER
    max = null,
    //infinite limits to the MAX_NUMBER
    step = 1,
    prefix,
    InputProps = {},
    onChange,
    onFocus = () => {},
    onBlur = () => {},
    value,
    labelMin = 'Minimum',
    labelMax = 'Maximum',
    label,
    ...otherProps
  } = props;
  const actualMin = useMemo(() => min == null ? MIN_NUMBER : min < MIN_NUMBER ? MIN_NUMBER : min, [min]);
  const actualMax = useMemo(() => max == null ? MAX_NUMBER : max > MAX_NUMBER ? MAX_NUMBER : max, [max]);
  const numberFieldInputProps = {
    type: "number",
    min: actualMin,
    max: actualMax,
    step
  };
  const id = useId(idOverride);
  const refMax = useRef(null);
  const [stateValue, setStateValue] = React.useState(value);
  const [error, setError] = React.useState(null);
  const [blurStateMin, setBlurStateMin] = React.useState(0);
  const [blurStateMax, setBlurStateMax] = React.useState(0);
  const [focusStateMin, setFocusStateMin] = React.useState(0);
  const [focusStateMax, setFocusStateMax] = React.useState(0);
  useEffect(() => {
    if (value != stateValue) {
      setStateValue(value);
    }
  }, [value]);
  const isMinMaxRangeValid = useMemo(() => {
    const minValueInt = stateValue[0];
    const maxValueInt = stateValue[1];
    const bothInputsHaveValue = minValueInt != null && maxValueInt != null;
    return !bothInputsHaveValue || bothInputsHaveValue && minValueInt <= maxValueInt;
  }, [stateValue]);
  useEffect(() => {
    if (value != stateValue && isMinMaxRangeValid) {
      onChange(stateValue);
    }
  }, stateValue);
  let {
    startAdornment
  } = InputProps;
  if (prefix) {
    startAdornment = /*#__PURE__*/React.createElement(PrefixContainer, null, prefix);
  }
  const commonProps = {
    startAdornment,
    onFocus: evt => onFocus(evt)
  };
  const changeRange = (e, isMin) => {
    const rawEventValue = e.target.value;
    //console.log("aaa", rawEventValue)
    let newValue = [...value];
    if (rawEventValue.trim() == "") {
      newValue[isMin ? 0 : 1] = null;
    } else {
      if (rawEventValue.indexOf(".") !== -1) {
        return;
      } // no floats
      if (rawEventValue.indexOf("e") !== -1) {
        return;
      } // no scientific notation
      let vInt = parseInt(rawEventValue);
      if (isNaN(vInt)) {
        return;
      }
      if (vInt > actualMax) {
        vInt = actualMax;
      }
      if (vInt < actualMin) {
        vInt = actualMin;
      }
      newValue[isMin ? 0 : 1] = vInt;
    }
    //console.log("be", newValue)
    setStateValue(newValue);
    setError(null);
  };
  const getValueMin = () => {
    return stateValue[0];
  };
  const getValueMax = () => {
    return stateValue[1];
  };
  const handleChangeMin = e => {
    changeRange(e, true);
  };
  const handleChangeMax = e => {
    changeRange(e, false);
  };
  const handleBlurMin = e => {
    onBlur(e);
    setBlurStateMin(blurStateMin + 1);
  };
  const handleBlurMax = e => {
    onBlur(e);
    setBlurStateMax(blurStateMax + 1);
  };
  const handleFocusMin = e => {
    onFocus(e);
    setFocusStateMin(focusStateMin + 1);
  };
  const handleFocusMax = e => {
    onFocus(e);
    setFocusStateMax(focusStateMax + 1);
  };
  useEffect(() => {
    if (blurStateMin > 0) {
      if (getValueMin() != null && !isNaN(getValueMax()) && getValueMin() > getValueMax()) {
        if (getValueMax() != null) {
          setStateValue([getValueMin(), getValueMin()]);
        } else {
          setStateValue([getValueMin(), null]);
        }
      }
    }
  }, [blurStateMin]);
  useEffect(() => {
    if (blurStateMax > 0) {
      if (getValueMax() != null && !isNaN(getValueMin()) && getValueMax() < getValueMin()) {
        if (getValueMin() != null) {
          setStateValue([getValueMax(), getValueMax()]);
        } else {
          setStateValue([null, getValueMax()]);
        }
      }
      setError(null);
    }
  }, [blurStateMax]);
  const minVal = stateValue[0];
  const maxVal = stateValue[1];
  useLayoutEffect(() => {
    //Layout effect ensures ref has been rendered
    if (focusStateMax > 0) {
      setTimeout(() => {
        var _refMax$current;
        refMax === null || refMax === void 0 ? void 0 : (_refMax$current = refMax.current) === null || _refMax$current === void 0 ? void 0 : _refMax$current.select();
      }, 100);
    }
  }, [focusStateMax]);
  const handleClearSearchMin = e => {
    setStateValue([null, getValueMax()]);
  };
  const handleClearSearchMax = e => {
    setStateValue([getValueMin(), null]);
  };
  return /*#__PURE__*/React.createElement(TextField, _extends({}, otherProps, {
    label: label,
    error: !!error,
    errorText: otherProps.errorText || error,
    isControlGrouped: true
  }), /*#__PURE__*/React.createElement(InputBase, _extends({
    inputProps: {
      ...props.inputProps,
      ...numberFieldInputProps,
      'aria-label': "".concat(label, ": ").concat(labelMin)
    }
  }, commonProps, {
    type: "number",
    error: !!error || !isMinMaxRangeValid,
    value: minVal != null ? minVal + "" : "" //!=null is important here as 0 is a valid value
    ,
    onChange: handleChangeMin,
    onBlur: handleBlurMin,
    onFocus: handleFocusMin,
    placeholder: labelMin,
    id: "".concat(id, "-min"),
    "data-testid": "min-input",
    endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      sx: {
        opacity: minVal != null ? 1 : 0.3,
        pointerEvents: minVal != null ? "all" : "none"
      }
    }, /*#__PURE__*/React.createElement(IconButton, {
      "aria-label": "clear search",
      onClick: handleClearSearchMin,
      onMouseDown: handleClearSearchMin,
      edge: "end",
      size: "small",
      tabIndex: -1
    }, /*#__PURE__*/React.createElement(ClearInputIcon, null)))
  })), /*#__PURE__*/React.createElement(RangeBetweenBox, null, /*#__PURE__*/React.createElement(RangeBetweenIcon, null)), /*#__PURE__*/React.createElement(InputBase, _extends({
    inputProps: {
      ...props.inputProps,
      ...numberFieldInputProps,
      ref: refMax,
      'aria-label': "".concat(label, ": ").concat(labelMax)
    }
  }, commonProps, {
    error: !!error || !isMinMaxRangeValid,
    value: maxVal != null ? maxVal + "" : "" //!=null is important here as 0 is a valid value
    ,
    onChange: handleChangeMax,
    onBlur: handleBlurMax,
    onFocus: handleFocusMax,
    placeholder: labelMax,
    "data-testid": "max-input",
    id: "".concat(id, "-max"),
    sx: {
      width: "100%"
    },
    endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      sx: {
        opacity: maxVal ? 1 : 0.3,
        pointerEvents: maxVal ? "all" : "none"
      }
    }, /*#__PURE__*/React.createElement(IconButton, {
      "aria-label": "clear search",
      onClick: handleClearSearchMax,
      onMouseDown: handleClearSearchMax,
      edge: "end",
      size: "small",
      tabIndex: -1
    }, /*#__PURE__*/React.createElement(ClearInputIcon, null)))
  })));
};
export default NumberRangeField;